import React, { useState } from 'react';
import { useIsMobile } from '@/hooks/use-mobile';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import MobileHeader from '@/components/mobile/MobileHeader';
import MobileBottomNav from '@/components/mobile/MobileBottomNav';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Mail, Phone, MessageCircle, HelpCircle, Users, Building, Palette, Heart, AlertCircle, UserPlus, Lock } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useInternalAuth } from '@/hooks/useInternalAuth';
import SupportTicketForm from '@/components/support/SupportTicketForm';

const Support = () => {
  const isMobile = useIsMobile();
  const { user: supabaseUser } = useAuth();
  const { user: internalUser } = useInternalAuth();
  const isAuthenticated = !!(supabaseUser || internalUser);
  const navigate = useNavigate();
  const [showTicketForm, setShowTicketForm] = useState(false);
  const [showLoginAlert, setShowLoginAlert] = useState(false);

  if (showTicketForm) {
    return (
      <div className="flex flex-col min-h-screen bg-black w-full">
        {!isMobile && <Header />}
        {isMobile && <MobileHeader />}
        
        <main className={`flex-grow ${
          isMobile 
            ? 'pt-16 mb-16 py-6 px-4' 
            : 'mt-24 mb-6 py-6 md:py-12 px-4 md:px-6'
        }`}>
          <div className="max-w-4xl mx-auto">
            <SupportTicketForm onClose={() => setShowTicketForm(false)} />
          </div>
        </main>
        
        {!isMobile && <Footer />}
        {isMobile && <MobileBottomNav />}
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-black w-full">
      {!isMobile && <Header />}
      {isMobile && <MobileHeader />}
      
      <main className={`flex-grow ${
        isMobile 
          ? 'pt-16 mb-16 py-6 px-4' 
          : 'mt-24 mb-6 py-6 md:py-12 px-4 md:px-6'
      }`}>
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">Support Center</h1>
            <p className="text-gray-400 text-lg">
              We're here to help you make the most of Players Lockeroom Connect
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <Card className="bg-[#0d1117] border border-gray-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Mail className="h-5 w-5 text-sports-green" />
                  Admin Support
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-400 mb-4">
                  Submit a support ticket directly to our admin team for help with your account, technical issues, or general questions.
                </p>
                <Button 
                  className="bg-sports-green text-black hover:bg-sports-green/90"
                  onClick={() => {
                    if (!isAuthenticated) {
                      setShowLoginAlert(true);
                      setTimeout(() => setShowLoginAlert(false), 5000);
                    } else {
                      setShowTicketForm(true);
                    }
                  }}
                >
                  Contact Support
                </Button>
              </CardContent>
            </Card>

            <Card className="bg-[#0d1117] border border-gray-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <HelpCircle className="h-5 w-5 text-sports-green" />
                  FAQ
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-400 mb-4">
                  Find answers to common questions about our platform.
                </p>
                <Button 
                  variant="outline" 
                  className="border-sports-green text-sports-green hover:bg-sports-green/10"
                  onClick={() => navigate('/faq')}
                >
                  View FAQ
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Login Alert for Non-Users */}
          {showLoginAlert && !isAuthenticated && (
            <Alert className="mb-6 bg-yellow-500/10 border border-yellow-500/20">
              <AlertCircle className="h-4 w-4 text-yellow-400" />
              <AlertDescription className="text-yellow-200">
                You need to create an account to contact our support team directly. This ensures we can respond to you and track your support requests properly.
                <div className="mt-3 flex gap-2">
                  <Link to="/register">
                    <Button size="sm" className="bg-yellow-500 text-black hover:bg-yellow-600">
                      <UserPlus className="h-3 w-3 mr-1" />
                      Create Account
                    </Button>
                  </Link>
                  <Link to="/faq">
                    <Button size="sm" variant="outline" className="border-yellow-500 text-yellow-400 hover:bg-yellow-500/10">
                      <HelpCircle className="h-3 w-3 mr-1" />
                      Browse FAQ
                    </Button>
                  </Link>
                </div>
              </AlertDescription>
            </Alert>
          )}

          {/* Common Support Topics - Updated Design */}
          <Card className="bg-[#0d1117] border border-gray-800 mb-8">
            <CardHeader>
              <CardTitle className="text-white text-xl flex items-center gap-3">
                <HelpCircle className="h-6 w-6 text-sports-green" />
                Common Support Topics
              </CardTitle>
              <p className="text-gray-400 text-sm">
                Browse these common areas where users need assistance
              </p>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card className="bg-gray-900/50 border border-gray-700 hover:border-gray-600 transition-colors">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <Lock className="h-6 w-6 text-sports-green mt-1 flex-shrink-0" />
                      <div>
                        <h3 className="text-white font-semibold mb-2">Account Issues</h3>
                        <p className="text-gray-400 text-sm mb-3">
                          Problems logging in, account verification, or profile setup
                        </p>
                        <div className="text-xs text-gray-500 space-y-1">
                          <div>• Login and authentication problems</div>
                          <div>• Password and PIN recovery</div>
                          <div>• Account verification issues</div>
                          <div>• Profile setup assistance</div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gray-900/50 border border-gray-700 hover:border-gray-600 transition-colors">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <Users className="h-6 w-6 text-sports-green mt-1 flex-shrink-0" />
                      <div>
                        <h3 className="text-white font-semibold mb-2">Team Management</h3>
                        <p className="text-gray-400 text-sm mb-3">
                          Creating teams, managing players, and team settings
                        </p>
                        <div className="text-xs text-gray-500 space-y-1">
                          <div>• Creating and organizing teams</div>
                          <div>• Player invitations and management</div>
                          <div>• Team settings and permissions</div>
                          <div>• Coach and ownership roles</div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gray-900/50 border border-gray-700 hover:border-gray-600 transition-colors">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <MessageCircle className="h-6 w-6 text-sports-green mt-1 flex-shrink-0" />
                      <div>
                        <h3 className="text-white font-semibold mb-2">Chat & Messaging</h3>
                        <p className="text-gray-400 text-sm mb-3">
                          Issues with team chat, notifications, or messaging features
                        </p>
                        <div className="text-xs text-gray-500 space-y-1">
                          <div>• Group chat creation and management</div>
                          <div>• Message delivery problems</div>
                          <div>• Notification settings</div>
                          <div>• Direct messaging issues</div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gray-900/50 border border-gray-700 hover:border-gray-600 transition-colors">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="h-6 w-6 text-sports-green mt-1 flex-shrink-0" />
                      <div>
                        <h3 className="text-white font-semibold mb-2">Technical Problems</h3>
                        <p className="text-gray-400 text-sm mb-3">
                          App crashes, loading issues, or feature not working properly
                        </p>
                        <div className="text-xs text-gray-500 space-y-1">
                          <div>• Application crashes and errors</div>
                          <div>• Slow loading or performance issues</div>
                          <div>• Feature malfunctions</div>
                          <div>• Browser compatibility problems</div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
      
      {!isMobile && <Footer />}
      {isMobile && <MobileBottomNav />}
    </div>
  );
};

export default Support;